import {observable, action, computed, makeAutoObservable} from "mobx";
import { StoreClass } from "../stores/Store";
import StepModel from "./StepModel";
import ProductModel from "../stores/ProductModel";
import ProductConfigurationOption from "./ProductConfigurationOption";
import FabricCollectionModel from "./FabricCollectionModel";
import { toBoolean } from "../utils/Booleans";
import { BUILT_IN_MATTRESS_OPTION_ID, BUILT_IN_MATTRESS_HARDNESS_OPTION_ID } from "../utils/ConfigurationOptions";


export default class OptionModel {
    store: StoreClass;
    configurationOption: ProductConfigurationOption;
    product: ProductModel;
    @observable id = null;
    @observable optionId = null;
    @observable productId = null;
    @observable valueId = null;
    @observable description = null;
    
    @observable quantity = 1;
    @observable price = 0;
    @observable name = null;
    @computed get label(){
        const type = this.stepType;
        const width = parseInt(store.getStepByType('radio_dimensions')?.selectedOption?.width) || 0;
        const extra_width = parseInt(store.dimensionAttributes.find(item => item.name === 'extra_width')?.value) || 0;
        if('select_headboard_width' === type){
            var add = parseInt(this.name) || 0;
            const sum = width + extra_width + add * 2;
            return `${sum} cm`;
        }
        return this.name;
    }
    @computed get productPrice() {
        const variantPrice = this.store.product.variants.find(v => v.option === this.optionId && v.value === this.valueId)?.price;
        if(variantPrice !== undefined){
            return Math.max(0, variantPrice - store.product.basePrice);
        }
        return this.price;
    }
    @computed get relativePrice(){
        const store = this.store;
        // const currentPrice = Math.max(0, this.productPrice - store.product.basePrice);
        if(this.step.selectedOption && this.step.selectedOption?.productPrice !== undefined){
            return this.productPrice - this.step.selectedOption.productPrice;
        }
        return 0;
    }
    @observable data: OptionDataType = {};
    /**
     *
     * @param {OptionDataType} props
     */
    constructor({ store, configurationOption, ...props }) {
        makeAutoObservable(this);
        
        if (!store && !configurationOption) {
            console.error({ store, configurationOption, props });
            throw Error(
                `store or configurationOption is undefined. One of them is required`
            );
        }
        if (configurationOption) {
            this.configurationOption = configurationOption;
            this.product = this.configurationOption.product;
            this.store = this.product.store;
        }
        if (store) {
            this.store = store;
        }

        this.fromJSON(props);
        // this.category = props.category;
    }

    @computed get variants():Array<{price: string, optionValues: Array<{option: number, value: number}>}> {
        return this.getExtraData("variants") || [];
    }

    /**
     * 
     * @param {int} optionId 
     * @param {int} valueId 
     * @returns {{price: string, optionValues: Array<{option: number, value: number} | undefined}
     */
    getVariantByOption(optionId, valueId) {
        const v = this.variants.find(v => v.optionValues.find(vv => vv.option === optionId && vv.value === valueId));
        return v;
    }
    @computed get can_buy() {
        return true;
    }
    @computed get order () {
        if(this.data?.order){
            return parseInt(this.data.order);
        }
        return 0;
    }
    @computed get url(){
        if(this.data?.url){
            return this.data.url;
        }
        if(this.product?.url){
            return this.product.url;
        }
        return null;
    }

    @computed get selectedPriceVariant(){
        return this.getVariantByOption(this.optionId, this.valueId);
    }

    @computed get imageUrl() {
        const overrided = this.getOverridedAttribute("image");
        if (overrided?.length > 0) {
            return overrided.at(0);
        }
        const images = this.getAttrbute("image");
        if (images?.length > 0) {
            return images.at(0);
        }
        return null;
    }
    @computed get imageFull() {
        const overrided = this.getOverridedAttribute("full_image");
        if (overrided?.length > 0) {
            return overrided.at(0);
        }

        const images = this.getAttrbute("full_image");
        if (images?.length > 0) {
            return images.at(0);
        }
        return null;
    }
    @computed get image() {
        return this.imageUrl;
    }
    @computed get ch_extra_data() {
        return this.data.ch_extra_data;
    }
    @computed get extra_data_attributes() {
        if(this.ch_extra_data && this.ch_extra_data.items?.length > 0){
            return this.ch_extra_data.items[0].attributes;
        }
        return null;
    }
    @computed get available_for() {
        return this.getAttrbute("available_for");
    }
    @computed get available_for_options() {
        return this.getAttrbute("available_for")?.options || [];
    }

    /**
     * 
     * @returns {Array<{OptionDataType}>}
     */
    @computed get override_fields() {
        return this.getAttrbute("override_fields");
    }

    getOverridedAttribute(name) {
        const store = this.store;
        if (this.override_fields?.length > 0) {
            const field = this.override_fields.find((f) => {
                const options:Array<{option_id: number, value_ids: number[]}> = f.attributes?.override_when_selected?.options || [];
                const hasMatchingOption = options.find((o) => {
                    const stepValue = store.getStepByOptionId(o.option_id)?.value;
                    return o.value_ids?.includes(stepValue);
                });
                return hasMatchingOption;
            });

            if (field) {
                return field.attributes[name];
            }
        }
        return null;
    }

    @computed get add_components() {
        return this.getAttrbute("add_components")
    }
    @computed get hasAddComponents() {
        return Boolean(this.add_components && this.add_components.length > 0);
    }

    @computed get mattress_for_whom() {
        return this.getExtraDataAttribute("mattress_for_whom") || [];
    }

    @computed get height() {
        var nr = this.getExtraDataAttribute("height", 0);
        if(nr){
            return parseInt(nr) || 0;
        }
        return 0;
    }
    @computed get extra_height() {
        var nr = this.getExtraDataAttribute("extra_height", 0);
        if(nr){
            return parseInt(nr) || 0;
        }
        return 0;
    }
    @computed get panel_width() {
        var nr = this.getExtraDataAttribute("panel_width", 0);
        if(nr){
            return parseInt(nr) || 0;
        }
        return 0;
    }

    /**
     *
     * @param {int} option_id Remote option id
     * @param {int} value_id Remote value id
     * @returns
     */
    canUseWhenSelected(option_id, value_id) {
        if (this.available_for_options.length > 0) {
            const option = this.available_for_options.find(
                (o) => o.option_id === option_id
            );
            if (option) {
                return option.value_ids.includes(value_id);
            }
        }
        return true;
    }

    @computed get step(): ?StepModel {
        return this.store.getStepByOptionId(this.optionId);
    }
    @computed get stepType() {
        return this.step?.type;
    }

    /**
     * If the option is available for selection.
     * Check if option selected in other steps allows for this option to be selected
     */
    @computed get isAvailable() {
        
        const frameStep = this.store.getStepByType("select_frame");
        const buildInMattressStep = this.store.getStepByType("select_built-in_mattress");
        const mattressStep = this.store.getStepByType("mattress");
        if (mattressStep?.selectedOption && this.isFullSize && this.store.canUseFullSizeMattress === false) {
            return false;
        }
        if (mattressStep?.selectedOption && this.isHalfSize && this.store.canUseHalfSizeMattress === false) {
            return false;
        }

        // step twardosc wbudowanego materaca && bonnel selected
        if(this.step.id === BUILT_IN_MATTRESS_HARDNESS_OPTION_ID && String(buildInMattressStep?.selectedOption?.name).match(/Bonnel/i)){
            return false;
        }

        if (this.available_for_options.length > 0) {
            return this.available_for_options.every(
                ({ option_id, value_ids }) => {
                    if(option_id === BUILT_IN_MATTRESS_OPTION_ID){
                        return true;
                    }
                    const step = this.store.getStepByOptionId(option_id);
                    if(!step){
                        return false;
                    }

                    return value_ids.includes(step.value);
                }
            );
        }
        return true;
    }

    @computed get itemName(){
        return this.getExtraData("name");
    }
    @computed get subtitle(){
        return this.getExtraDataAttribute("subtitle");
    }

    fromJSON(props) {
        this.data = props;
        this.name = props.name;
        if(this.itemName){
            this.name = this.itemName;
        }

        this.id = parseInt(props.id);
        this.description = this.getAttrbute("subtitle");

        const isMainOption = !this.product;
        if (isMainOption) {
            try {
                this.optionId = this.data.productOptions[0].optionId;
                this.productId = parseInt(this.data.productOptions[0].valueId);
                this.valueId = parseInt(this.data.productOptions[0].valueId);
            } catch (err) {
                console.error(this);
                throw Error(`Invalid productOptions for ${this.name}:` + err);
            }
        } else {
            this.productId = this.product.id;
            this.valueId = this.id;
            this.optionId = this.configurationOption.id;
        }
        if (isNaN(this.productId)) {
            throw Error("productId is not a number");
        }

        if (isNaN(this.valueId)) {
            throw Error("valueId is not a number");
        }

        if (isNaN(this.id)) {
            console.error(this.data);
            throw Error("id is not a number " + this.id);
        }
        this.price = parseFloat(this.data.price);
        var p = this.getExtraData('price');
        if(p){
            this.price = parseFloat(p);
        }
    }


    // fabrics
    @computed get color(){
        const fabric = this.store.fabrics.find((f) => f.valueId === this.valueId);
        if(fabric) {
            return fabric.color;
        }
        return null;
    }
    @computed get fabricCategory(): FabricCollectionModel {
        const fabric = this.store.fabrics.find((f) => f.valueId === this.valueId);
        if(fabric) {
            return fabric.category;
        }
        return null;
    }
    @computed get fabricCategoryName(): FabricCollectionModel {
        const cat = this.fabricCategory;
        if(cat){
            return cat.name;
        }
        
        return null;
    }

    @computed get width() {
        let width = this.getAttrbute("width");
        const overrided = this.getOverridedAttribute("width");
        if (overrided !== null) {
            width = overrided;
        }
        if(width === null){
            width = this.getExtraDataAttribute("width");
        }
        return parseFloat(width) ?? null;
    }
    @computed get length() {
        let length = this.getAttrbute("length");
        const overrided = this.getOverridedAttribute("length");
        if (overrided !== null) {
            length = overrided;
        }

        if(length === null){
            length = this.getExtraDataAttribute("length");
        }
        return parseFloat(length) ?? null;
    }

    @computed get attributes() {
        const overrided = this.getOverridedAttribute("properties");
        if (overrided !== null) {
            return overrided;
        }
        return this.getAttrbute("properties") || [];
    }

    @computed get learn_more(){
        const overrided = this.getOverridedAttribute('learn_more');
        if (overrided !== null) {
            return overrided;
        }
        return this.getAttrbute('learn_more');
    }

    @computed get isHalfSize() {
        const store = this.store;
        if (store) {
            return store.mattressHalfSize.some(
                (f) => f.valueId === this.valueId
            );
        }
        return false;
    }

    @computed get isFullSize() {
        const store = this.store;
        if (store) {
            return store.mattressFullSize.some(
                (f) => f.valueId === this.valueId
            );
        }
        return false;
    }

    @computed get full_materac_size(): Array<{
        id: number,
        optionId: number,
        productId: number,
        valueId: number,
        name: string,
    }> {
        const full_materac_size =
            this.getExtraDataAttribute("full_materac_size");
        if (full_materac_size) {
            return full_materac_size.map((f) => {
                const productOptions = f.productOptions || [];
                return {
                    id: f.id,
                    optionId: productOptions[0]?.optionId,
                    productId: productOptions[0]?.valueId,
                    valueId: productOptions[0]?.valueId,
                    name: f.name,
                };
            });
        }
        return null;
    }

    @computed get additional_materac_size(): Array<{
        id: number,
        optionId: number,
        productId: number,
        valueId: number,
        name: string,
    }> {
        const additional_materac_size =
            this.getExtraDataAttribute("additional_materac_size");
        if (additional_materac_size) {
            return additional_materac_size.map((f) => {
                const productOptions = f.productOptions || [];
                return {
                    id: f.id,
                    optionId: productOptions[0]?.optionId,
                    productId: productOptions[0]?.valueId,
                    valueId: productOptions[0]?.valueId,
                    name: f.name,
                };
            });
        }
        return null;
    }

    @computed get half_materac_size(): Array<{
        id: number,
        optionId: number,
        productId: number,
        valueId: number,
        name: string,
    }> {
        const half_materac_size =
            this.getExtraDataAttribute("half_materac_size");
        if (half_materac_size) {
            return half_materac_size.map((f) => {
                const productOptions = f.productOptions || [];
                return {
                    id: f.id,
                    optionId: productOptions[0]?.optionId,
                    productId: productOptions[0]?.valueId,
                    valueId: productOptions[0]?.valueId,
                    name: f.name,
                };
            });
        }
        return null;
    }

    @computed get custom_size() {
        const custom_size = (this.getExtraDataAttribute("custom_size"));
        return toBoolean(custom_size);
       
    }
    @computed get cannot_be_full() {
        const cannot_be_full = (this.getExtraDataAttribute("cannot_be_full"));
        return toBoolean(cannot_be_full);
       
    }
    @computed get cannot_be_half() {
        const cannot_be_half = (this.getExtraDataAttribute("cannot_be_half"));
        return toBoolean(cannot_be_half);
       
    }
    @computed get allow_only_mattres() {
        const ids = (this.getExtraDataAttribute("allow_only_mattres"));
        return ids || [];
    }

    getAttrbute(name) {
        if (this.data.attributes && this.data.attributes[name] !== undefined) {
            return this.data.attributes[name];
        }
        return null;
    }

    getExtraData(name) {
        if (
            this.data.ch_extra_data?.items.length > 0 &&
            this.data.ch_extra_data.items[0][name] !== undefined
        ) {
            return this.data.ch_extra_data.items[0][name];
        }
        return null;
    }
    getExtraDataAttribute(name, _default = null) {
        const attributes = this.getExtraData("attributes");
        if (attributes && attributes[name] !== undefined) {
            return attributes[name];
        }
        return _default;
    }

    getStepByOptionId(option_id){
        return this.store.getStepByOptionId(option_id);
    }

    toString() {
        return `OptionModel: ${this.id} - ${this.name}`;
    }

    toJson() {
        return this.data;
    }

    static create(data) {
        return new OptionModel(data);
    }
}



export class OptionDataType{
    id = 0;
    name = 'Undefined';
    image = null;
    texture = {
        name: 'Undefined',
        image: null
    };
    price = 0;
    productOptions: Array<{optionId: number, valueId: number}> = [];
    attributes:Array<any> = [];
    
}